/**
 * A 100% composition bar and its legend, shared by the family Dashboard's Asset Allocation
 * card and the firm Overview's "Where it is held" and Prospects cards (steward-app#1885).
 *
 * Presentational only: the caller decides what the slices ARE (asset groups, custody buckets,
 * prospect stages) and hands over a label, a share, a colour and the figure to print. Nothing
 * here knows about money, assets or prospects, which is what lets three cards share one look.
 */

// ── Palette ─────────────────────────────────────────────────────────────────
// Luminance-stepped brand tints (navy → slate → bronze family), assigned by rank. Color here
// *separates* segments; identity is carried by the always-present legend + direct labels
// (never color alone), and a 2px surface gap sits between fills — so the low-chroma
// quiet-luxury set stays readable, CVD included.
// Allocation segment colors — a fixed categorical set, theme-aware so the dark
// end doesn't vanish on the dark surface (and vice-versa). Validated with the
// dataviz palette checker per mode: light passes adjacent-pair CVD ΔE ≈ 15;
// dark's tightest low-chroma pairs sit in the 8–12 floor band, which is legal
// here because the legend labels + 2px surface gaps are the required secondary
// encoding (identity is never color-alone). The low chroma is the intended
// quiet-luxury look, not a defect.
// The two warm entries deliberately do NOT track --color-bronze. When the accent
// was quieted to #A1855F (2026-08-25) the obvious tidy-up was to retint these to
// match; measured, it makes the chart worse. Holding each entry's lightness and
// moving it to the new accent's hue/chroma drops adjacent-pair separation from
// 23.2/10.4/12.7 to 18.7/9.7/9.8 OKLab dE, i.e. below the ~15 this set is
// validated at, because the quieter accent has less chroma to separate it from
// the neighbouring blue-greys. A categorical palette optimises for distance
// between segments; the UI accent optimises for restraint. They are allowed to
// disagree, and here they must.
//
// The EIGHTH entry of each list (added for the Prospects card, steward-app#1885, when it had
// eight stages) was chosen by eye and was NOT run through the OKLab separation checker that
// validated the first seven; no such checker script exists in this repo. What was measured
// (WCAG contrast against --color-surface, 2026-09-28): light #8F8460 on #FFFFFF 3.73:1, dark
// #B07D45 on #1C1F27 4.60:1, both above the 3:1 non-text floor. Adjacent-pair distance to the
// seventh entry was not measured. The Dashboard never reaches this entry (it draws at most
// seven slices), and the Prospects legend carries every stage's name, count and share, so
// identity never rests on this colour. The Prospects card now has nine stages (steward-app#2260)
// and puts its own Invited colour IN FRONT of these eight (`INVITED_COLOR` in AdvisorOverview.tsx),
// so this list stays at eight and every other stage keeps its colour.
export const ALLOC_COLORS_LIGHT = ['#0F172A', '#33435C', '#586882', '#CD7F32', '#9E6A38', '#6E7A8E', '#94A3B8', '#8F8460'];
export const ALLOC_COLORS_DARK  = ['#EDEAE2', '#BFC5CF', '#949CAB', '#D9A066', '#9C6330', '#727C8D', '#5A6474', '#B07D45'];

export function allocationColors(theme: 'light' | 'dark'): string[] {
  return theme === 'dark' ? ALLOC_COLORS_DARK : ALLOC_COLORS_LIGHT;
}

export interface CompositionSlice {
  label: string;
  /** Share of the whole, 0 to 100. A zero share draws no segment but still gets a legend row. */
  pct: number;
  color: string;
  /** What the legend prints beside the label (a compact amount, a count). */
  valueText: string;
  /** Native tooltip on the segment. Optional: the legend already carries the same facts. */
  title?: string;
  /** Legend row is dimmed (a zero-count stage stays listed, quietly). */
  dimmed?: boolean;
}

/**
 * The bar. `ariaLabel` gives it a text equivalent as an image; without one the bar is
 * `aria-hidden`, which is right when the legend beside it already states every slice.
 */
export function CompositionBar({ slices, ariaLabel }: { slices: CompositionSlice[]; ariaLabel?: string }) {
  const drawn = slices.filter(s => s.pct > 0);
  return (
    <div
      className="flex h-3.5 w-full gap-[2px] overflow-hidden rounded-full"
      {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : { 'aria-hidden': true })}
    >
      {drawn.map(s => (
        <div
          key={s.label}
          className="h-full first:rounded-l-full last:rounded-r-full"
          style={{ width: `${s.pct}%`, backgroundColor: s.color, minWidth: 3 }}
          title={s.title}
        />
      ))}
    </div>
  );
}

/**
 * The legend: swatch, name, figure and percent per slice. `className` sets the column
 * layout, so each card chooses how many columns its width can hold.
 */
export function CompositionLegend({ slices, className, hidePercent }: {
  slices: CompositionSlice[]; className: string;
  /** Drop the percent column (a share would be over a denominator the caller cannot vouch for). */
  hidePercent?: boolean;
}) {
  return (
    <div className={className}>
      {slices.map(s => (
        <div key={s.label} className={`flex items-center gap-2 min-w-0${s.dimmed ? ' text-slate' : ''}`} data-dimmed={s.dimmed ? 'true' : undefined}>
          <span
            className={`h-2.5 w-2.5 shrink-0 rounded-[2px]${s.dimmed ? ' opacity-35' : ''}`}
            style={{ backgroundColor: s.color }}
            aria-hidden
          />
          <span className={`truncate font-sans text-[12px] ${s.dimmed ? 'font-normal text-slate' : 'font-medium text-body'}`}>{s.label}</span>
          <span className={`ml-auto shrink-0 font-sans text-[11px] tabular-nums ${s.dimmed ? 'text-slate' : 'text-body'}`}>{s.valueText}</span>
          {!hidePercent && (
            <span className="w-9 shrink-0 text-right font-sans text-[11px] tabular-nums text-slate">{s.pct.toFixed(0)}%</span>
          )}
        </div>
      ))}
    </div>
  );
}
